import React, { useState } from 'react';
import { useDocuments } from './hooks';
import DocumentsContent from './DocumentsContent';
import { DocumentModal, ImportModal } from '.';
import { UploadProgressModal } from './UploadProgressModal';
import { useAdminTranslation } from '../../../modules/i18n';

type ConfirmDialogState = { isOpen: boolean; title: string; message: string; onConfirm: () => void; isLoading?: boolean };

interface DocumentsContainerProps {
  confirmDialog: ConfirmDialogState;
  setConfirmDialog: (dialog: ConfirmDialogState | ((prev: ConfirmDialogState) => ConfirmDialogState)) => void;
}

const DocumentsContainer: React.FC<DocumentsContainerProps> = ({ confirmDialog, setConfirmDialog }) => {
  const { t } = useAdminTranslation();
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

  const {
    // State
    documents,
    folders,
    loading,
    error,
    showDocumentModal,
    editingDocument,
    showImportModal,
    importFolderId,
    deletingDocumentIds,
    deletingFolderIds,

    // Actions
    handleCreateDocument,
    handleEditDocument,
    handleDeleteDocument,
    handleDeleteDocuments,
    handleDeleteSelection,
    handleSaveDocument,
    handleImportToFolder,
    handleImportFiles: _handleImportFiles,
    handleDeleteFolder,
    handleUploadFolder,
    handleImportComplete,

    // Modal controls
    setShowDocumentModal,
    setShowImportModal,

    // Lazy loading controls
    loadFolderDocuments,
    prefetchFolderDocuments,
    isFolderLoading,
    getFolderDocuments
  } = useDocuments(confirmDialog, setConfirmDialog);

  return (
    <div className='flex h-full flex-col'>
      <DocumentsContent
        documents={documents}
        folders={folders}
        loading={loading}
        error={error}
        onCreateDocument={handleCreateDocument}
        onEditDocument={handleEditDocument}
        onDeleteDocument={handleDeleteDocument}
        onDeleteDocuments={handleDeleteDocuments}
        onDeleteSelection={handleDeleteSelection}
        onImportToFolder={handleImportToFolder}
        onDeleteFolder={handleDeleteFolder}
        deletingDocumentIds={deletingDocumentIds}
        deletingFolderIds={deletingFolderIds}
        selectedFolders={selectedFolders}
        setSelectedFolders={setSelectedFolders}
        selectedDocuments={selectedDocuments}
        setSelectedDocuments={setSelectedDocuments}
        loadFolderDocuments={loadFolderDocuments}
        prefetchFolderDocuments={prefetchFolderDocuments}
        isFolderLoading={isFolderLoading}
        getFolderDocuments={getFolderDocuments}
        onUploadFolder={handleUploadFolder}
      />

      {/* Document Modal */}
      {showDocumentModal && (
        <DocumentModal
          isOpen={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          onSave={handleSaveDocument}
          editingDocument={editingDocument}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onComplete={handleImportComplete}
          folderId={importFolderId}
          folderName={folders.find((f) => f.id === importFolderId)?.name || t('content.documents.common.folder')}
        />
      )}

      {/* Upload Progress Modal - Phase 4: Real-Time Progress UI */}
      <UploadProgressModal />
    </div>
  );
};

export default DocumentsContainer;
